import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

const sortTasksByPriority = (tasks) =>
  [...tasks].sort((a, b) => {
    const priorityDiff = (b.priority ?? 2) - (a.priority ?? 2);
    if (priorityDiff !== 0) return priorityDiff;
    return (b.id ?? 0) - (a.id ?? 0);
  });

export const addTaskTracker = createAsyncThunk(
  "taskTracker/add",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/task_tracker/add",
        formData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTaskTrackerList = createAsyncThunk(
  "taskTracker/list",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/task_tracker/list", {});
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDoneTaskTrackerList = createAsyncThunk(
  "taskTracker/listDone",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/task_tracker/list_done", {
        page,
        limit,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTaskTracker = createAsyncThunk(
  "taskTracker/edit",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/task_tracker/edit",
        formData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  "taskTracker/updateStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/task_tracker/update_status",
        payload
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTaskPriority = createAsyncThunk(
  "taskTracker/updatePriority",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/task_tracker/update_priority",
        payload
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const addTaskComment = createAsyncThunk(
  "taskTracker/addComment",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/task_tracker/add_comment",
        payload
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const taskTrackerSlice = createSlice({
  name: "taskTracker",
  initialState: {
    list: [],
    doneList: [],
    doneListStatus: "idle",
    donePagination: {
      page: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 1,
    },
    requestStatus: "idle",
    commentStatus: "idle",
    statusUpdateStatus: "idle",
    priorityUpdateStatus: "idle",
    editStatus: "idle",
    message: "",
    commentMessage: "",
    statusUpdateMessage: "",
    priorityUpdateMessage: "",
    editMessage: "",
  },
  reducers: {
    resetTaskTrackerStatus: (state) => {
      state.requestStatus = "idle";
      state.message = "";
    },
    resetEditStatus: (state) => {
      state.editStatus = "idle";
      state.editMessage = "";
    },
    resetCommentStatus: (state) => {
      state.commentStatus = "idle";
      state.commentMessage = "";
    },
    resetStatusUpdateStatus: (state) => {
      state.statusUpdateStatus = "idle";
      state.statusUpdateMessage = "";
    },
    resetPriorityUpdateStatus: (state) => {
      state.priorityUpdateStatus = "idle";
      state.priorityUpdateMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTaskTracker.pending, (state) => {
        state.requestStatus = "loading";
      })
      .addCase(addTaskTracker.fulfilled, (state, action) => {
        state.requestStatus = action.payload?.status ? "success" : "error";
        state.message = action.payload?.message || "";
      })
      .addCase(addTaskTracker.rejected, (state) => {
        state.requestStatus = "error";
        state.message = "Something went wrong";
      })
      .addCase(fetchTaskTrackerList.fulfilled, (state, action) => {
        state.list = sortTasksByPriority(action.payload?.data || []);
      })
      .addCase(fetchDoneTaskTrackerList.pending, (state) => {
        state.doneListStatus = "loading";
      })
      .addCase(fetchDoneTaskTrackerList.fulfilled, (state, action) => {
        state.doneListStatus = "idle";
        state.doneList = action.payload?.data || [];
        state.donePagination = action.payload?.pagination || {
          page: 1,
          limit: 10,
          totalItems: 0,
          totalPages: 1,
        };
      })
      .addCase(fetchDoneTaskTrackerList.rejected, (state) => {
        state.doneListStatus = "error";
        state.doneList = [];
      })
      .addCase(addTaskComment.pending, (state) => {
        state.commentStatus = "loading";
      })
      .addCase(addTaskComment.fulfilled, (state, action) => {
        state.commentStatus = action.payload?.status ? "success" : "error";
        state.commentMessage = action.payload?.message || "";
        if (action.payload?.status && action.payload?.data?.task_id) {
          const taskId = action.payload.data.task_id;
          const comments = action.payload.data.comments || [];
          state.list = state.list.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  comments,
                  comment_count: comments.length,
                }
              : task
          );
        }
      })
      .addCase(addTaskComment.rejected, (state) => {
        state.commentStatus = "error";
        state.commentMessage = "Something went wrong";
      })
      .addCase(updateTaskTracker.pending, (state) => {
        state.editStatus = "loading";
      })
      .addCase(updateTaskTracker.fulfilled, (state, action) => {
        state.editStatus = action.payload?.status ? "success" : "error";
        state.editMessage = action.payload?.message || "";
        if (action.payload?.status && action.payload?.data?.task_id) {
          const { task_id, title, description, images } = action.payload.data;
          state.list = state.list.map((task) =>
            task.id === task_id
              ? { ...task, title, description, images: images || [] }
              : task
          );
        }
      })
      .addCase(updateTaskTracker.rejected, (state) => {
        state.editStatus = "error";
        state.editMessage = "Something went wrong";
      })
      .addCase(updateTaskStatus.pending, (state) => {
        state.statusUpdateStatus = "loading";
      })
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        state.statusUpdateStatus = action.payload?.status ? "success" : "error";
        state.statusUpdateMessage = action.payload?.message || "";
        if (action.payload?.status && action.payload?.data?.task_id) {
          const { task_id, status, show_status } = action.payload.data;
          if (status === 2) {
            state.list = state.list.filter((task) => task.id !== task_id);
          } else {
            state.list = state.list.map((task) =>
              task.id === task_id
                ? { ...task, status, show_status }
                : task
            );
          }
        }
      })
      .addCase(updateTaskStatus.rejected, (state) => {
        state.statusUpdateStatus = "error";
        state.statusUpdateMessage = "Something went wrong";
      })
      .addCase(updateTaskPriority.pending, (state) => {
        state.priorityUpdateStatus = "loading";
      })
      .addCase(updateTaskPriority.fulfilled, (state, action) => {
        state.priorityUpdateStatus = action.payload?.status ? "success" : "error";
        state.priorityUpdateMessage = action.payload?.message || "";
        if (action.payload?.status && action.payload?.data?.task_id) {
          const { task_id, priority, show_priority } = action.payload.data;
          state.list = sortTasksByPriority(
            state.list.map((task) =>
              task.id === task_id
                ? { ...task, priority, show_priority }
                : task
            )
          );
        }
      })
      .addCase(updateTaskPriority.rejected, (state) => {
        state.priorityUpdateStatus = "error";
        state.priorityUpdateMessage = "Something went wrong";
      });
  },
});

export const {
  resetTaskTrackerStatus,
  resetCommentStatus,
  resetStatusUpdateStatus,
  resetPriorityUpdateStatus,
  resetEditStatus,
} = taskTrackerSlice.actions;
export default taskTrackerSlice.reducer;
